import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import type { PdfDocumentMeta, Project } from '@canvas/shared-types';
import { projectPath, userProjectsPath } from '@canvas/shared-types';
import { getClientDB } from '@canvas/firebase-config/client';
import { useAuth } from '@/hooks/useAuth';
import AuthScreen from '@/components/AuthScreen';
import Dashboard, { type DashTab } from '@/components/Dashboard';
import EditorView from '@/components/EditorView';
import MovieEditorView from '@/components/movie/MovieEditorView';
import PdfEditorView from '@/components/pdf/PdfEditorView';
import {
  deletePdfDocument,
  fetchPdfBytesFromStorage,
  listPdfDocuments,
  uploadPdfDocument,
} from '@/lib/pdf/pdfUpload';
import { idbPutPdfSource, pickPdfFile } from '@/lib/pdf/pdfSourceIdb';
import PdfImagesToPdfModal, {
  filesToImagePages,
  type PdfImagePageItem,
} from '@/components/pdf/PdfImagesToPdfModal';
import PdfScanCameraModal from '@/components/pdf/PdfScanCameraModal';
import VerifyEmailBanner from '@/components/VerifyEmailBanner';
import { MOTION_TEMPLATES, motionTemplateById } from '@/data/motionTemplates';
import { decodeFirestoreValue, encodeFirestoreValue } from '@/lib/firestoreSafe';
import { requestEditorSave } from '@/lib/editorSave';
import { copyDesignToMovie, createMovieDocument, isMovieProject } from '@/lib/movieProject';
import { useProjectStore } from '@/stores/projectStore';

type View = 'dashboard' | 'editor' | 'movie' | 'pdf';

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

async function writeProjectDoc(project: Project): Promise<void> {
  const db = getClientDB();
  await setDoc(
    doc(db, path(projectPath(project.userId, project.projectId))),
    encodeFirestoreValue({
      ...project,
      createdAt: Timestamp.fromDate(project.createdAt),
      lastModifiedAt: Timestamp.fromDate(project.lastModifiedAt),
      updatedAt: Timestamp.fromDate(project.updatedAt),
    }) as Record<string, unknown>
  );
}

function normalizeProject(data: Record<string, unknown>, fallbackId: string): Project {
  const decoded = decodeFirestoreValue(data) as Record<string, unknown>;
  return {
    ...(decoded as unknown as Project),
    projectId: (decoded.projectId as string) || fallbackId,
    createdAt: toDate(decoded.createdAt),
    lastModifiedAt: toDate(decoded.lastModifiedAt),
    updatedAt: toDate(decoded.updatedAt),
  };
}

interface PdfOpenPayload {
  file?: File | null;
  docId?: string | null;
  bytes?: ArrayBuffer | null;
  name?: string | null;
}

export default function App() {
  const {
    user,
    loading: authLoading,
    error: authError,
    clearError,
    signInEmail,
    signUpEmail,
    signInGoogle,
    resendVerificationEmail,
    refreshEmailVerified,
    logout,
  } = useAuth();

  const [view, setView] = useState<View>('dashboard');
  const [dashTab, setDashTab] = useState<DashTab>('projects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [pdfDocs, setPdfDocs] = useState<PdfDocumentMeta[]>([]);
  const [pdfDocsLoading, setPdfDocsLoading] = useState(false);
  const [openingPdfId, setOpeningPdfId] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState<PdfOpenPayload | null>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const [imagesModalOpen, setImagesModalOpen] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [imageSeedPages, setImageSeedPages] = useState<PdfImagePageItem[] | null>(null);
  const [imageSeedKey, setImageSeedKey] = useState(0);
  const [imageAppendBatch, setImageAppendBatch] = useState<PdfImagePageItem[] | null>(null);
  const [imageAppendKey, setImageAppendKey] = useState(0);
  const [imageExistingCount, setImageExistingCount] = useState(0);

  const loadProjects = useCallback(async (uid: string) => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const db = getClientDB();
      const snap = await getDocs(collection(db, path(userProjectsPath(uid))));
      const list = snap.docs
        .map((item) => normalizeProject(item.data(), item.id))
        .sort(
          (a, b) => b.lastModifiedAt.getTime() - a.lastModifiedAt.getTime()
        );
      setProjects(list);
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const loadPdfDocs = useCallback(async (uid: string) => {
    setPdfDocsLoading(true);
    try {
      const list = await listPdfDocuments(uid);
      setPdfDocs(list);
    } catch (err) {
      console.warn('Failed to load PDF documents', err);
      setPdfDocs([]);
    } finally {
      setPdfDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      void loadProjects(user.uid);
      void loadPdfDocs(user.uid);
      setView('dashboard');
      setActiveProject(null);
      setPdfOpen(null);
    } else {
      setProjects([]);
      setActiveProject(null);
      setPdfDocs([]);
      setPdfOpen(null);
    }
  }, [user, loadProjects, loadPdfDocs]);

  const handleCreateProject = async (input: {
    name: string;
    description: string;
    width: number;
    height: number;
    tags: string[];
  }) => {
    if (!user) return;
    setCreating(true);
    setProjectsError(null);

    try {
      const projectId = crypto.randomUUID();
      const now = new Date();

      const project: Project = {
        projectId,
        userId: user.uid,
        name: input.name,
        description: input.description,
        canvasData: {
          version: '5.3.0',
          objects: [],
          width: input.width,
          height: input.height,
          background: '#ffffff',
        },
        width: input.width,
        height: input.height,
        tags: input.tags,
        kind: 'design',
        status: 'draft',
        isPublished: false,
        collaborators: [],
        versionCount: 1,
        fileSize: 0,
        viewCount: 0,
        forkCount: 0,
        commentsEnabled: true,
        usesAI: false,
        createdAt: now,
        lastModifiedAt: now,
        lastModifiedBy: user.uid,
        updatedAt: now,
        showAuthorOnPublished: true,
      };

      await writeProjectDoc(project);

      setProjects((prev) => [project, ...prev]);
      setActiveProject(project);
      setDashTab('projects');
      setView('editor');
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to create project');
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const handleCreateMovie = async (input: { name: string; templateId: string }) => {
    if (!user) return;
    const template = motionTemplateById(input.templateId) ?? MOTION_TEMPLATES[0];
    if (!template) return;
    setCreating(true);
    setProjectsError(null);
    try {
      const project = createMovieDocument({
        userId: user.uid,
        name: input.name,
        template,
      });
      await writeProjectDoc(project);
      setProjects((prev) => [project, ...prev]);
      setActiveProject(project);
      setDashTab('movie');
      setView('movie');
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to create movie');
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const handleMakeMovie = async (projectId: string) => {
    if (!user) return;
    setCreating(true);
    setProjectsError(null);
    try {
      await requestEditorSave();
      const live = useProjectStore.getState().currentProject;
      let source: Project | null =
        live && live.projectId === projectId ? live : null;
      if (!source) {
        const db = getClientDB();
        const snap = await getDoc(doc(db, path(projectPath(user.uid, projectId))));
        if (!snap.exists()) {
          setProjectsError('Project not found');
          return;
        }
        source = normalizeProject(snap.data() as Record<string, unknown>, snap.id);
      }
      const movie = copyDesignToMovie(source, user.uid);
      await writeProjectDoc(movie);
      setProjects((prev) => [movie, ...prev.filter((item) => item.projectId !== movie.projectId)]);
      setActiveProject(movie);
      setDashTab('movie');
      setView('movie');
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to make a movie');
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!user) return;
    try {
      const db = getClientDB();
      await deleteDoc(doc(db, path(projectPath(user.uid, projectId))));
      setProjects((prev) => prev.filter((p) => p.projectId !== projectId));
      if (activeProject?.projectId === projectId) {
        setActiveProject(null);
        setView('dashboard');
      }
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };

  const handleOpenProject = async (projectId: string) => {
    if (!user) return;
    setProjectsError(null);
    try {
      // Always fetch the latest from Firestore — the dashboard list can be stale
      // after an earlier edit session, and opening stale data can wipe a good save.
      const db = getClientDB();
      const snap = await getDoc(doc(db, path(projectPath(user.uid, projectId))));
      if (!snap.exists()) {
        setProjectsError('Project not found');
        return;
      }
      const project = normalizeProject(snap.data() as Record<string, unknown>, snap.id);
      setProjects((prev) => {
        const without = prev.filter((p) => p.projectId !== project.projectId);
        return [project, ...without].sort(
          (a, b) => b.lastModifiedAt.getTime() - a.lastModifiedAt.getTime()
        );
      });
      setActiveProject(project);
      setView(isMovieProject(project) ? 'movie' : 'editor');
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to open project');
    }
  };

  const handleOpenPdfPicker = () => {
    pdfFileInputRef.current?.click();
  };

  const handlePdfFileChosen = (file: File | null) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setProjectsError('Please choose a PDF file');
      return;
    }
    setPdfOpen({ file });
    setView('pdf');
  };

  const openImagesModal = (pages: PdfImagePageItem[] = []) => {
    setProjectsError(null);
    setImageSeedPages(pages);
    setImageSeedKey((k) => k + 1);
    setImageAppendBatch(null);
    setImagesModalOpen(true);
  };

  const handleUploadImages = () => {
    setProjectsError(null);
    // Must stay in the user-gesture stack (no setTimeout) or the picker is blocked
    imageFileInputRef.current?.click();
  };

  const handleImagesChosen = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    // Snapshot before the input resets — FileList is live and becomes empty
    const pages = filesToImagePages(Array.from(files));
    if (!pages.length) {
      setProjectsError('Please choose image files (JPG, PNG, WebP…)');
      return;
    }
    openImagesModal(pages);
  };

  const handleScan = () => {
    setProjectsError(null);
    setImageExistingCount(0);
    setCameraModalOpen(true);
  };

  const handleCameraDone = (pages: PdfImagePageItem[]) => {
    setCameraModalOpen(false);
    if (!pages.length) return;
    if (imagesModalOpen) {
      setImageAppendBatch(pages);
      setImageAppendKey((k) => k + 1);
    } else {
      openImagesModal(pages);
    }
  };

  const handleImagesPdfCreated = (payload: { bytes: ArrayBuffer; name: string }) => {
    setImagesModalOpen(false);
    setImageSeedPages(null);
    setImageAppendBatch(null);
    setPdfOpen({
      bytes: payload.bytes,
      name: payload.name,
    });
    setView('pdf');
  };

  const handleOpenSavedPdf = async (docMeta: PdfDocumentMeta) => {
    if (openingPdfId) return;
    setOpeningPdfId(docMeta.docId);
    setProjectsError(null);
    try {
      let bytes: ArrayBuffer | null = null;

      try {
        bytes = await fetchPdfBytesFromStorage(docMeta.storagePath, docMeta.docId);
      } catch (storageErr) {
        console.warn('Storage/cache open failed, prompting for local file', storageErr);
        // CORS often blocks Storage downloads — let the user re-attach the PDF once
        const file = await pickPdfFile();
        if (!file) {
          throw storageErr instanceof Error
            ? storageErr
            : new Error('Failed to open PDF');
        }
        bytes = await file.arrayBuffer();
        await idbPutPdfSource(docMeta.docId, bytes);
        // Best-effort refresh of the Storage object for later devices
        void uploadPdfDocument({
          bytes,
          name: docMeta.name || file.name,
          pageCount: docMeta.pageCount || 1,
          docId: docMeta.docId,
        }).catch((err) => console.warn('PDF re-upload after restore failed', err));
      }

      if (!bytes || bytes.byteLength < 8) {
        throw new Error('PDF is empty or corrupt.');
      }

      setPdfOpen({
        docId: docMeta.docId,
        bytes,
        name: docMeta.name,
      });
      setView('pdf');
    } catch (err) {
      console.error('Open saved PDF failed', docMeta, err);
      setProjectsError(err instanceof Error ? err.message : 'Failed to open PDF');
    } finally {
      setOpeningPdfId(null);
    }
  };

  const handleDeletePdf = async (docMeta: PdfDocumentMeta) => {
    if (!user) return;
    try {
      await deletePdfDocument(user.uid, docMeta);
      setPdfDocs((prev) => prev.filter((d) => d.docId !== docMeta.docId));
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to delete PDF');
    }
  };

  if (authLoading) {
    return <div className="loading-screen">Connecting to Craftara…</div>;
  }

  if (!user) {
    return (
      <AuthScreen
        error={authError}
        onClearError={clearError}
        onSignIn={signInEmail}
        onSignUp={signUpEmail}
        onGoogle={signInGoogle}
      />
    );
  }

  if (view === 'pdf' && pdfOpen) {
    return (
      <>
        {user && !user.emailVerified && (
          <VerifyEmailBanner
            email={user.email}
            onResend={resendVerificationEmail}
            onRefresh={refreshEmailVerified}
          />
        )}
        <PdfEditorView
          userId={user.uid}
          initialFile={pdfOpen.file}
          initialBytes={pdfOpen.bytes}
          initialName={pdfOpen.name}
          initialDocId={pdfOpen.docId}
          onClose={() => {
            setView('dashboard');
            setPdfOpen(null);
            void loadPdfDocs(user.uid);
          }}
          onSentToCanvas={(project) => {
            setProjects((prev) => [project, ...prev.filter((p) => p.projectId !== project.projectId)]);
            setActiveProject(project);
            setPdfOpen(null);
            setView('editor');
          }}
        />
      </>
    );
  }

  if (view === 'movie' && activeProject) {
    return (
      <>
        {user && !user.emailVerified && (
          <VerifyEmailBanner
            email={user.email}
            onResend={resendVerificationEmail}
            onRefresh={refreshEmailVerified}
          />
        )}
        <MovieEditorView
          project={activeProject}
          onBack={() => {
            setDashTab('movie');
            setView('dashboard');
            setActiveProject(null);
            void loadProjects(user.uid);
            void loadPdfDocs(user.uid);
          }}
        />
      </>
    );
  }

  if (view === 'editor' && activeProject) {
    return (
      <>
        {user && !user.emailVerified && (
          <VerifyEmailBanner
            email={user.email}
            onResend={resendVerificationEmail}
            onRefresh={refreshEmailVerified}
          />
        )}
        <EditorView
          project={activeProject}
          studio="design"
          onMakeMovie={() => {
            void handleMakeMovie(activeProject.projectId);
          }}
          onBack={() => {
            setDashTab('projects');
            setView('dashboard');
            setActiveProject(null);
            void loadProjects(user.uid);
            void loadPdfDocs(user.uid);
          }}
        />
      </>
    );
  }

  return (
    <>
      {user && !user.emailVerified && (
        <VerifyEmailBanner
          email={user.email}
          onResend={resendVerificationEmail}
          onRefresh={refreshEmailVerified}
        />
      )}
      <input
        ref={pdfFileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          e.target.value = '';
          handlePdfFileChosen(file);
        }}
      />
      <input
        ref={imageFileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          // Snapshot files before reset — FileList empties when value is cleared
          const list = e.target.files ? Array.from(e.target.files) : [];
          e.target.value = '';
          handleImagesChosen(list);
        }}
      />
      <Dashboard
        user={user}
        projects={projects}
        loading={projectsLoading}
        error={projectsError}
        creating={creating}
        pdfDocs={pdfDocs}
        pdfDocsLoading={pdfDocsLoading}
        openingPdfId={openingPdfId}
        onLogout={() => void logout()}
        onOpenProject={(projectId) => {
          void handleOpenProject(projectId);
        }}
        onDeleteProject={handleDeleteProject}
        onCreateProject={handleCreateProject}
        onCreateMovie={handleCreateMovie}
        onMakeMovie={handleMakeMovie}
        initialTab={dashTab}
        onTabChange={setDashTab}
        onOpenPdf={handleOpenPdfPicker}
        onUploadImages={handleUploadImages}
        onScan={handleScan}
        onOpenSavedPdf={(docId) => {
          const meta = pdfDocs.find((d) => d.docId === docId);
          if (meta) void handleOpenSavedPdf(meta);
        }}
        onDeletePdf={(docId) => {
          const meta = pdfDocs.find((d) => d.docId === docId);
          if (meta) void handleDeletePdf(meta);
        }}
      />
      <PdfImagesToPdfModal
        open={imagesModalOpen}
        seedPages={imageSeedPages}
        seedKey={imageSeedKey}
        appendBatch={imageAppendBatch}
        appendKey={imageAppendKey}
        onClose={() => {
          setImagesModalOpen(false);
          setImageSeedPages(null);
          setImageAppendBatch(null);
        }}
        onCreated={handleImagesPdfCreated}
        onOpenCamera={(currentPageCount) => {
          setImageExistingCount(currentPageCount);
          setCameraModalOpen(true);
        }}
      />
      <PdfScanCameraModal
        open={cameraModalOpen}
        existingCount={imagesModalOpen ? imageExistingCount : 0}
        onClose={() => setCameraModalOpen(false)}
        onDone={handleCameraDone}
      />
    </>
  );
}
